import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LuBan, LuCalendarPlus, LuCircleCheck, LuClock, LuCreditCard, LuLock, LuPencil, LuPlay,
  LuPlus, LuReceipt, LuSettings2, LuTriangleAlert, LuWallet, LuX,
} from 'react-icons/lu';
import API from '../../api/axios';
import AppLayout from '../../components/layout/AppLayout';
import { Badge, Button, Card, EmptyState, Grid, Loader, Page, PageHeader, StatCard, Tabs } from '../../components/ui';
import { C, ROLES, fmtF } from '../../theme';
import { ETATS } from '../proprietaire/Abonnement';
import '../../components/abonnement/Abonnement.css';

const dateFr = d => (d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const msgErreur = e => e?.response?.data?.message?.replace(/^❌\s*/, '') || 'Erreur réseau';
const STATUT_TX = { en_attente: ['En attente', 'warn'], paye: ['Payé', 'success'], echoue: ['Échoué', 'danger'] };

export default function Abonnements() {
  const [onglet, setOnglet] = useState('etablissements');
  const [toast, setToast] = useState('');
  const notifier = useCallback(t => { setToast(t); setTimeout(() => setToast(''), 4000); }, []);

  return (
    <AppLayout role="admin">
      <Page>
        {toast && <div role="status" className={`ab-toast ${toast.startsWith('❌') ? 'is-err' : ''}`}>{toast.replace(/^(✅|❌)\s*/, '')}</div>}
        <PageHeader title="Abonnements" subtitle="Établissements, plans, remises, paiements en ligne et soldes prépayés" />
        <Tabs
          tabs={[
            { id: 'etablissements', label: 'Établissements', icon: LuCreditCard },
            { id: 'plans', label: 'Plans', icon: LuSettings2 },
            { id: 'durees', label: 'Durées & remises', icon: LuClock },
            { id: 'paiements', label: 'Paiements & soldes', icon: LuWallet },
          ]}
          value={onglet} onChange={setOnglet}
        />
        {onglet === 'etablissements' && <VueEtablissements notifier={notifier} />}
        {onglet === 'plans' && <VuePlans notifier={notifier} />}
        {onglet === 'durees' && <VueDurees notifier={notifier} />}
        {onglet === 'paiements' && <VuePaiements notifier={notifier} />}
      </Page>
    </AppLayout>
  );
}

// ════════════════════════ ÉTABLISSEMENTS ════════════════════════
function VueEtablissements({ notifier }) {
  const [donnees, setDonnees] = useState(null);
  const [plans, setPlans] = useState([]);
  const [filtre, setFiltre] = useState('tous');
  const [recherche, setRecherche] = useState('');
  const [action, setAction] = useState(null); // { type: 'paiement'|'jours', abo }

  const charger = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([API.get('/abonnements'), API.get('/abonnements/plans?tous=1')]);
      setDonnees(a.data);
      setPlans(p.data.plans || []);
    } catch (e) { notifier('❌ ' + msgErreur(e)); }
  }, [notifier]);
  useEffect(() => { charger(); }, [charger]);

  const liste = useMemo(() => (donnees?.abonnements || []).filter(a => {
    if (filtre === 'alerte' && !['expire_bientot', 'grace'].includes(a.etat)) return false;
    if (filtre === 'bloques' && !['lecture_seule', 'suspendu'].includes(a.etat)) return false;
    if (filtre === 'actifs' && a.etat !== 'actif') return false;
    const q = recherche.trim().toLowerCase();
    return !q || `${a.etablissement_nom} ${a.proprietaire_prenom || ''} ${a.proprietaire_nom || ''}`.toLowerCase().includes(q);
  }), [donnees, filtre, recherche]);

  const executer = async (requete, succes) => {
    try { const r = await requete(); notifier(r.data.message || succes); charger(); } catch (e) { notifier('❌ ' + msgErreur(e)); }
  };

  if (!donnees) return <Loader fullScreen={false} />;
  const r = donnees.resume;

  return (
    <>
      <Grid min={170} style={{ marginBottom: 20 }}>
        <StatCard icon={LuCircleCheck} label="Actifs" value={r.actifs} color={C.success} />
        <StatCard icon={LuClock} label="Expirent sous 7 jours" value={r.expire_bientot} color={C.warn} />
        <StatCard icon={LuTriangleAlert} label="En période de grâce" value={r.grace} color={C.danger} />
        <StatCard icon={LuLock} label="Lecture seule / suspendus" value={r.expires + r.suspendus} color={C.danger} />
        <StatCard icon={LuWallet} label="Encaissé ce mois" value={`${fmtF(r.encaisse_mois)}`} hint="hors paiements par solde" color={C.info} />
      </Grid>

      <div className="ab-actions" style={{ marginBottom: 14 }}>
        <input className="ab-input" style={{ maxWidth: 280 }} placeholder="Rechercher un établissement…" value={recherche} onChange={e => setRecherche(e.target.value)} />
        {[['tous', 'Tous'], ['alerte', 'À relancer'], ['bloques', 'Bloqués'], ['actifs', 'Actifs']].map(([id, label]) => (
          <Button key={id} variant={filtre === id ? 'primary' : 'secondary'} style={{ minHeight: 36 }} onClick={() => setFiltre(id)}>{label}</Button>
        ))}
      </div>

      <Card style={{ padding: 0 }}>
        {liste.length === 0 ? <EmptyState icon={LuCreditCard} title="Aucun établissement" /> : (
          <div className="ab-table-wrap">
            <table className="ab-table">
              <thead><tr><th>Établissement</th><th>Propriétaire</th><th>Plan</th><th>Échéance</th><th>État</th><th>Auto</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
              <tbody>
                {liste.map(a => {
                  const e = ETATS[a.etat] || ETATS.aucun;
                  return (
                    <tr key={a.etablissement_id}>
                      <td><strong>{a.etablissement_nom}</strong><div className="ab-sub">{a.etablissement_type}</div></td>
                      <td>{a.proprietaire_id ? `${a.proprietaire_prenom || ''} ${a.proprietaire_nom || ''}` : <span className="ab-sub">—</span>}</td>
                      <td>{a.plan_nom || '—'}{a.prix_mensuel && <div className="ab-sub ab-num">{fmtF(a.prix_mensuel)}/mois</div>}</td>
                      <td className="ab-num">{dateFr(a.date_fin)}{a.id && <div className="ab-sub">{a.jours_restants > 0 ? `${a.jours_restants} j` : `il y a ${-a.jours_restants} j`}</div>}</td>
                      <td><Badge tone={e.tone}>{e.label}</Badge></td>
                      <td>{a.id ? (a.renouvellement_auto ? `Oui · ${a.duree_renouvellement} m` : 'Non') : '—'}</td>
                      <td>
                        <div className="ab-actions" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                          <Button variant="secondary" icon={LuReceipt} style={{ minHeight: 34, padding: '0 10px' }} onClick={() => setAction({ type: 'paiement', abo: a })}>Paiement</Button>
                          {a.id && <Button variant="secondary" icon={LuCalendarPlus} style={{ minHeight: 34, padding: '0 10px' }} onClick={() => setAction({ type: 'jours', abo: a })} aria-label="Offrir des jours" />}
                          {a.id && (a.etat === 'suspendu'
                            ? <Button variant="secondary" icon={LuPlay} style={{ minHeight: 34, padding: '0 10px' }} onClick={() => executer(() => API.patch(`/abonnements/${a.etablissement_id}/reactiver`))}>Réactiver</Button>
                            : <Button variant="danger" icon={LuBan} style={{ minHeight: 34, padding: '0 10px' }} onClick={() => setAction({ type: 'suspendre', abo: a })} aria-label="Suspendre" />)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {action && (
        <ModaleAction action={action} plans={plans} onClose={() => setAction(null)}
          onFait={msg => { setAction(null); notifier(msg); charger(); }} notifier={notifier} />
      )}
    </>
  );
}

function ModaleAction({ action, plans, onClose, onFait, notifier }) {
  const a = action.abo;
  const [f, setF] = useState({ montant: '', mode_paiement: 'especes', mois_payes: '1', reference: '', note: '', plan_id: a.plan_id || plans[0]?.id || '', jours: '7', motif: '' });
  const [envoi, setEnvoi] = useState(false);
  const plan = plans.find(p => p.id === Number(f.plan_id));
  useEffect(() => {
    if (action.type === 'paiement' && plan) setF(x => ({ ...x, montant: String(Number(plan.prix_mensuel) * Number(x.mois_payes || 1)) }));
  }, [action.type, plan, f.mois_payes]);

  const valider = async e => {
    e.preventDefault();
    setEnvoi(true);
    try {
      let r;
      if (action.type === 'paiement') {
        r = await API.post('/abonnements/paiement', {
          etablissement_id: a.etablissement_id, plan_id: Number(f.plan_id), montant: Number(f.montant),
          mode_paiement: f.mode_paiement, mois_payes: Number(f.mois_payes), reference: f.reference, note: f.note,
        });
      } else if (action.type === 'jours') {
        r = await API.post(`/abonnements/${a.etablissement_id}/offrir`, { jours: Number(f.jours), motif: f.motif });
      } else {
        r = await API.patch(`/abonnements/${a.etablissement_id}/suspendre`);
      }
      onFait(r.data.message);
    } catch (err) { notifier('❌ ' + msgErreur(err)); setEnvoi(false); }
  };

  const titres = { paiement: 'Enregistrer un paiement', jours: 'Offrir des jours', suspendre: "Suspendre l'abonnement" };
  return createPortal(
    <div className="ab-modal-fond" style={{ '--accent': ROLES.admin.accent }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="ab-modal" role="dialog" aria-modal="true" onSubmit={valider}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div><h2>{titres[action.type]}</h2><div className="ab-sub">{a.etablissement_nom}</div></div>
          <button type="button" className="ab-bandeau-fermer" aria-label="Fermer" onClick={onClose}><LuX aria-hidden /></button>
        </div>
        {action.type === 'paiement' && (
          <>
            <p className="ab-sub" style={{ margin: 0 }}>Paiement reçu hors ligne (espèces, virement, Mobile Money direct). L'abonnement est prolongé à partir de l'échéance actuelle.</p>
            <div className="ab-grille">
              <label className="ab-champ">Plan
                <select className="ab-input" value={f.plan_id} onChange={e => setF({ ...f, plan_id: e.target.value })}>
                  {plans.map(p => <option key={p.id} value={p.id}>{p.nom} — {fmtF(p.prix_mensuel)}/mois{p.actif === false ? ' (inactif)' : ''}</option>)}
                </select>
              </label>
              <label className="ab-champ">Nombre de mois
                <input className="ab-input" type="number" min="1" max="36" required value={f.mois_payes} onChange={e => setF({ ...f, mois_payes: e.target.value })} />
              </label>
              <label className="ab-champ">Montant reçu (FCFA)
                <input className="ab-input" type="number" min="0" required value={f.montant} onChange={e => setF({ ...f, montant: e.target.value })} />
              </label>
              <label className="ab-champ">Mode
                <select className="ab-input" value={f.mode_paiement} onChange={e => setF({ ...f, mode_paiement: e.target.value })}>
                  <option value="especes">Espèces</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="virement">Virement</option>
                </select>
              </label>
              <label className="ab-champ">Référence
                <input className="ab-input" value={f.reference} onChange={e => setF({ ...f, reference: e.target.value })} placeholder="N° de reçu, de transaction…" />
              </label>
              <label className="ab-champ">Note
                <input className="ab-input" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} />
              </label>
            </div>
          </>
        )}
        {action.type === 'jours' && (
          <div className="ab-grille">
            <label className="ab-champ">Jours offerts
              <input className="ab-input" type="number" min="1" max="365" required value={f.jours} onChange={e => setF({ ...f, jours: e.target.value })} />
            </label>
            <label className="ab-champ">Motif
              <input className="ab-input" value={f.motif} onChange={e => setF({ ...f, motif: e.target.value })} placeholder="Geste commercial, essai prolongé…" />
            </label>
          </div>
        )}
        {action.type === 'suspendre' && (
          <p style={{ margin: 0 }}>L'établissement passera immédiatement en <strong>lecture seule</strong> (ventes et stock bloqués) jusqu'à sa réactivation.</p>
        )}
        <div className="ab-actions" style={{ justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" variant={action.type === 'suspendre' ? 'danger' : 'primary'} disabled={envoi}>
            {action.type === 'suspendre' ? 'Suspendre' : 'Valider'}
          </Button>
        </div>
      </form>
    </div>,
    document.body
  );
}

// ════════════════════════ PLANS ════════════════════════
const PLAN_VIDE = { nom: '', prix_mensuel: '', max_produits: '', max_utilisateurs: '', description: '' };

function VuePlans({ notifier }) {
  const [plans, setPlans] = useState(null);
  const [form, setForm] = useState(null);
  const charger = useCallback(async () => {
    try { setPlans((await API.get('/abonnements/plans?tous=1')).data.plans || []); } catch (e) { notifier('❌ ' + msgErreur(e)); }
  }, [notifier]);
  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async e => {
    e.preventDefault();
    try {
      const corps = { ...form, prix_mensuel: Number(form.prix_mensuel) };
      const r = form.id ? await API.put(`/abonnements/plans/${form.id}`, corps) : await API.post('/abonnements/plans', corps);
      notifier(r.data.message); setForm(null); charger();
    } catch (err) { notifier('❌ ' + msgErreur(err)); }
  };
  const basculer = async p => {
    try { const r = await API.put(`/abonnements/plans/${p.id}`, { actif: p.actif === false }); notifier(r.data.message); charger(); } catch (err) { notifier('❌ ' + msgErreur(err)); }
  };

  if (!plans) return <Loader fullScreen={false} />;
  return (
    <>
      <div className="ab-actions" style={{ justifyContent: 'flex-end', marginBottom: 14 }}>
        {!form && <Button icon={LuPlus} onClick={() => setForm({ ...PLAN_VIDE })}>Nouveau plan</Button>}
      </div>
      {form && (
        <Card title={form.id ? `Modifier « ${form.nom} »` : 'Nouveau plan'} style={{ marginBottom: 16 }}>
          <form onSubmit={enregistrer} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="ab-grille">
              <label className="ab-champ">Nom<input className="ab-input" required value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} /></label>
              <label className="ab-champ">Prix mensuel (FCFA)<input className="ab-input" type="number" min="0" required value={form.prix_mensuel} onChange={e => setForm({ ...form, prix_mensuel: e.target.value })} /></label>
              <label className="ab-champ">Produits max (vide = illimité)<input className="ab-input" type="number" min="1" value={form.max_produits || ''} onChange={e => setForm({ ...form, max_produits: e.target.value })} /></label>
              <label className="ab-champ">Utilisateurs max (vide = illimité)<input className="ab-input" type="number" min="1" value={form.max_utilisateurs || ''} onChange={e => setForm({ ...form, max_utilisateurs: e.target.value })} /></label>
            </div>
            <label className="ab-champ">Description (affichée au client)<input className="ab-input" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></label>
            <div className="ab-actions"><Button type="submit">Enregistrer</Button><Button type="button" variant="secondary" onClick={() => setForm(null)}>Annuler</Button></div>
          </form>
        </Card>
      )}
      <div className="ab-etabs">
        {plans.map(p => (
          <article key={p.id} className={`ab-etab ${p.actif === false ? '' : 'is-actif'}`}>
            <div className="ab-etab-tete">
              <div><div className="ab-etab-nom">{p.nom}</div><div className="ab-sub">{p.description || 'Sans description'}</div></div>
              <Badge tone={p.actif === false ? 'neutral' : 'success'}>{p.actif === false ? 'Inactif' : 'Proposé'}</Badge>
            </div>
            <div className="ab-solde-montant" style={{ fontSize: 24 }}>{fmtF(p.prix_mensuel)} <span className="ab-sub" style={{ fontSize: 14 }}>/ mois</span></div>
            <div className="ab-lignes">
              <div className="ab-ligne"><span>Produits</span><strong>{p.max_produits || 'Illimité'}</strong></div>
              <div className="ab-ligne"><span>Utilisateurs</span><strong>{p.max_utilisateurs || 'Illimité'}</strong></div>
            </div>
            <div className="ab-actions">
              <Button variant="secondary" icon={LuPencil} onClick={() => setForm({ ...p })}>Modifier</Button>
              <Button variant={p.actif === false ? 'secondary' : 'danger'} onClick={() => basculer(p)}>{p.actif === false ? 'Proposer à nouveau' : 'Retirer de la vente'}</Button>
            </div>
          </article>
        ))}
      </div>
      <p className="ab-sub" style={{ marginTop: 12 }}>Un plan retiré de la vente reste valable pour les établissements qui l'ont déjà ; il n'est plus proposé aux nouveaux paiements.</p>
    </>
  );
}

// ════════════════════════ DURÉES & REMISES ════════════════════════
function VueDurees({ notifier }) {
  const [durees, setDurees] = useState(null);
  const [nouvelle, setNouvelle] = useState('');
  useEffect(() => {
    API.get('/abonnements/durees?tous=1').then(r => setDurees(r.data.durees)).catch(e => notifier('❌ ' + msgErreur(e)));
  }, [notifier]);

  const maj = (mois, champs) => setDurees(ds => ds.map(d => (d.mois === mois ? { ...d, ...champs } : d)));
  const enregistrer = async () => {
    try { const r = await API.put('/abonnements/durees', { durees }); notifier(r.data.message); } catch (e) { notifier('❌ ' + msgErreur(e)); }
  };
  const ajouter = () => {
    const m = parseInt(nouvelle, 10);
    if (!(m > 0 && m <= 36) || durees.some(d => d.mois === m)) return;
    setDurees([...durees, { mois: m, remise_pct: 0, actif: true }].sort((a, b) => a.mois - b.mois));
    setNouvelle('');
  };

  if (!durees) return <Loader fullScreen={false} />;
  return (
    <Card title="Durées proposées et remises" style={{ maxWidth: 640 }}>
      <p className="ab-sub" style={{ marginTop: 0 }}>Exemple : 12 mois à −15 % = le client paie 10,2 mois. Les remises s'appliquent aux paiements en ligne, par solde et au renouvellement automatique.</p>
      <div className="ab-table-wrap">
        <table className="ab-table">
          <thead><tr><th>Durée</th><th>Remise (%)</th><th>Proposée</th></tr></thead>
          <tbody>
            {durees.map(d => (
              <tr key={d.mois}>
                <td><strong>{d.mois} mois</strong></td>
                <td><input className="ab-input" style={{ maxWidth: 110 }} type="number" min="0" max="90" step="0.5" value={d.remise_pct} onChange={e => maj(d.mois, { remise_pct: e.target.value })} aria-label={`Remise ${d.mois} mois`} /></td>
                <td>
                  <label className="ab-switch">
                    <input type="checkbox" checked={d.actif !== false} onChange={e => maj(d.mois, { actif: e.target.checked })} />
                    <span className="ab-switch-piste" aria-hidden />
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ab-actions" style={{ marginTop: 14 }}>
        <input className="ab-input" style={{ maxWidth: 140 }} type="number" min="1" max="36" placeholder="Ex : 24" value={nouvelle} onChange={e => setNouvelle(e.target.value)} aria-label="Nouvelle durée en mois" />
        <Button variant="secondary" icon={LuPlus} onClick={ajouter}>Ajouter une durée</Button>
        <span style={{ flex: 1 }} />
        <Button onClick={enregistrer}>Enregistrer</Button>
      </div>
    </Card>
  );
}

// ════════════════════════ PAIEMENTS & SOLDES ════════════════════════
function VuePaiements({ notifier }) {
  const [donnees, setDonnees] = useState(null);
  const [credit, setCredit] = useState(null); // { proprietaire_id, nom, montant, motif }
  const charger = useCallback(async () => {
    try { setDonnees((await API.get('/abonnements/admin/transactions')).data); } catch (e) { notifier('❌ ' + msgErreur(e)); }
  }, [notifier]);
  useEffect(() => { charger(); }, [charger]);

  const lancer = async () => {
    try { const r = await API.post('/abonnements/admin/lancer-tache'); notifier(r.data.message); } catch (e) { notifier('❌ ' + msgErreur(e)); }
  };
  const crediter = async e => {
    e.preventDefault();
    try {
      const r = await API.post(`/abonnements/soldes/${credit.proprietaire_id}/crediter`, { montant: Number(credit.montant), motif: credit.motif });
      notifier(r.data.message); setCredit(null); charger();
    } catch (err) { notifier('❌ ' + msgErreur(err)); }
  };

  if (!donnees) return <Loader fullScreen={false} />;
  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <div className="ab-solde">
          <div>
            <div className="ab-solde-libelle">Paiement en ligne (CinetPay)</div>
            <div style={{ marginTop: 6 }}>
              {donnees.simulation ? <Badge tone="warn">Mode test (simulation)</Badge>
                : donnees.sandbox ? <Badge tone="warn">Sandbox CinetPay (monnaie virtuelle)</Badge>
                : donnees.paiement_en_ligne ? <Badge tone="success">Production</Badge> : <Badge tone="neutral">Non configuré</Badge>}
            </div>
            <p className="ab-sub" style={{ margin: '8px 0 0', maxWidth: '70ch' }}>
              Les renouvellements automatiques et les rappels (J-7, J-3, J-1, échéance, puis pendant la grâce) sont vérifiés toutes les heures.
            </p>
          </div>
          <Button variant="secondary" icon={LuPlay} onClick={lancer}>Vérifier maintenant</Button>
        </div>
      </Card>

      <Card title="Soldes prépayés" style={{ marginBottom: 16 }}>
        {donnees.soldes.length === 0 ? <p className="ab-sub" style={{ margin: 0 }}>Aucun propriétaire n'a encore de solde.</p> : (
          <div className="ab-table-wrap">
            <table className="ab-table">
              <thead><tr><th>Propriétaire</th><th style={{ textAlign: 'right' }}>Solde</th><th style={{ textAlign: 'right' }}>Action</th></tr></thead>
              <tbody>
                {donnees.soldes.map(s => (
                  <tr key={s.proprietaire_id}>
                    <td>{s.prenom} {s.nom}</td>
                    <td className="ab-num" style={{ textAlign: 'right', fontWeight: 700 }}>{fmtF(s.solde)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Button variant="secondary" icon={LuPlus} style={{ minHeight: 34 }} onClick={() => setCredit({ proprietaire_id: s.proprietaire_id, nom: `${s.prenom} ${s.nom}`, montant: '', motif: 'Espèces reçues' })}>Créditer</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="ab-sub" style={{ marginBottom: 0 }}>Pour créditer un propriétaire qui n'apparaît pas ici, enregistrez d'abord une recharge depuis son espace, ou utilisez « Paiement » dans l'onglet Établissements.</p>
      </Card>

      <Card title="Transactions en ligne">
        {donnees.transactions.length === 0 ? <p className="ab-sub" style={{ margin: 0 }}>Aucune transaction.</p> : (
          <div className="ab-table-wrap">
            <table className="ab-table">
              <thead><tr><th>Date</th><th>Client</th><th>Objet</th><th>Moyen</th><th>Statut</th><th style={{ textAlign: 'right' }}>Montant</th></tr></thead>
              <tbody>
                {donnees.transactions.map(t => {
                  const [label, ton] = STATUT_TX[t.statut] || [t.statut, 'neutral'];
                  return (
                    <tr key={t.transaction_id}>
                      <td className="ab-num">{dateFr(t.created_at)}<div className="ab-sub">{t.transaction_id}</div></td>
                      <td>{t.prenom} {t.nom}</td>
                      <td>{t.objet === 'recharge' ? 'Recharge du solde' : `${t.etablissement_nom} · ${t.mois} mois${t.plan_nom ? ` (${t.plan_nom})` : ''}`}</td>
                      <td>{t.methode || '—'}</td>
                      <td><Badge tone={ton}>{label}</Badge></td>
                      <td className="ab-num" style={{ textAlign: 'right', fontWeight: 700 }}>
                        {fmtF(t.montant)}
                        {Number(t.frais) > 0 && <div className="ab-sub" style={{ fontWeight: 400 }}>+ {fmtF(t.frais)} de frais</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {credit && createPortal(
        <div className="ab-modal-fond" style={{ '--accent': ROLES.admin.accent }} onClick={e => { if (e.target === e.currentTarget) setCredit(null); }}>
          <form className="ab-modal" role="dialog" aria-modal="true" onSubmit={crediter}>
            <h2>Créditer le solde</h2>
            <div className="ab-sub">{credit.nom}</div>
            <div className="ab-grille">
              <label className="ab-champ">Montant (FCFA)<input className="ab-input" type="number" min="1" required autoFocus value={credit.montant} onChange={e => setCredit({ ...credit, montant: e.target.value })} /></label>
              <label className="ab-champ">Motif<input className="ab-input" value={credit.motif} onChange={e => setCredit({ ...credit, motif: e.target.value })} /></label>
            </div>
            <div className="ab-actions" style={{ justifyContent: 'flex-end' }}>
              <Button type="button" variant="secondary" onClick={() => setCredit(null)}>Annuler</Button>
              <Button type="submit">Créditer</Button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </>
  );
}

